import React from 'react';
import { Award, Briefcase, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { usePortfolio } from '@/context/PortfolioContext';
import {
  SectionShell,
  SectionInner,
  SectionHeader,
  SectionLoading,
  SectionError,
} from '@/components/section/sectionLayout';

const About = () => {
  const { portfolioData, loading, error } = usePortfolio();

  if (loading) return <SectionLoading id="about" />;
  if (error) return <SectionError id="about" error={error} />;

  const about = portfolioData?.about;
  const profileSrc = about?.image?.startsWith('http')
    ? about.image
    : about?.image
      ? `/${about.image.replace(/^\//, '')}`
      : '/images/profile.png';

  const stats = [
    { icon: Award, label: "Années d'expérience", value: about?.yearsOfExperience || 0 },
    { icon: Briefcase, label: 'Projets complétés', value: about?.projectsCompleted || 0 },
    { icon: Users, label: 'Clients satisfaits', value: about?.clientsSatisfied || 0 },
  ];

  return (
    <SectionShell id="about" variant="alt">
      <SectionInner>
        <SectionHeader title="À propos de" highlight="moi" />

        <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 opacity-30 blur-lg" />
            <img
              onError={(e) => {
                e.currentTarget.src = '/images/profile.png';
              }}
              loading="lazy"
              src={profileSrc}
              alt="Photo de profil"
              className="relative aspect-square w-full rounded-2xl object-cover shadow-2xl sm:aspect-[5/5]"
            />
          </div>

          <div className="space-y-5 sm:space-y-6">
            <p className="text-base text-justify leading-relaxed text-gray-700 dark:text-gray-300 sm:text-lg">
              {about?.description || 'Description non disponible'}
            </p>

            <p className="text-sm text-justify leading-relaxed text-gray-600 dark:text-gray-400 sm:text-base">
              Spécialisé dans l&apos;écosystème JavaScript moderne, je maîtrise aussi bien le développement
              frontend avec React que le backend avec Node.js et Express. Ma passion pour le DevOps me
              permet d&apos;accompagner mes projets de la conception jusqu&apos;au déploiement en production.
            </p>

            <div className="grid grid-cols-1 gap-3 pt-2 min-[480px]:grid-cols-3 sm:gap-4 sm:pt-4">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card
                    key={index}
                    className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e] motion-safe:transition-shadow motion-safe:hover:shadow-lg motion-safe:hover:shadow-emerald-500/10"
                  >
                    <CardContent className="p-4 text-center sm:p-5">
                      <Icon className="mx-auto mb-2 h-6 w-6 text-emerald-400" />
                      <div className="mb-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
                        {stat.value}+
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">{stat.label}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </SectionInner>
    </SectionShell>
  );
};

export default About;
